import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@iziping/common";
import { LogOut, Menu, Settings, UserCircle, X } from "lucide-react";
import styles from "./NavBar.module.css";

export { styles as navBarStyles };

export interface UserMenuItem {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

interface NavBarProps {
  logo?: string;
  onSettingsClick?: () => void;
  userMenuItems?: UserMenuItem[];
  children?: React.ReactNode;
  version?: string;
}

export function NavBar({
  logo,
  onSettingsClick,
  userMenuItems,
  children,
  version,
}: NavBarProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (user?.settings?.language) {
      i18n.changeLanguage(user.settings.language);
    }
  }, [user?.settings?.language, i18n]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    useAuthStore.getState().logout();
    navigate("/login");
  };

  return (
    <header
      className={`${styles.header}${mobileOpen ? ` ${styles.headerOpen}` : ""}`}
      ref={menuRef}
    >
      <nav className={styles.navContainer}>
        <div className={styles.navBar}>
          {logo && (
            <div className={styles.logoContainer}>
              <img src={logo} alt="logo" className={styles.logo} />
            </div>
          )}

          {/* Desktop menu */}
          <ul className={styles.menuCenter}>{children}</ul>

          {/* Desktop user dropdown */}
          <li
            className={`${styles.menuDropdown} ${styles.menuRight} ${styles.desktopOnly}`}
          >
            {user && (
              <>
                <span className={styles.menuA}>
                  {user.settings?.logoUrl && (
                    <img
                      src={user.settings.logoUrl}
                      alt="logo club"
                      className={styles.clubLogo}
                    />
                  )}
                  <UserCircle size={32} className={styles.clubLogoDefault} />
                  {user.firstname} {user.lastname}
                </span>
                <ul className={styles.menuSubmenu}>
                  {onSettingsClick && (
                    <li className={styles.menuA} onClick={onSettingsClick}>
                      <Settings size={15} className={styles.menuItemIcon} />
                      {t("MENU_MANAGEMENT")}
                    </li>
                  )}
                  {userMenuItems?.map((item) => (
                    <li
                      key={item.label}
                      className={styles.menuA}
                      onClick={item.onClick}
                    >
                      <span className={styles.menuItemIcon}>{item.icon}</span>
                      {item.label}
                    </li>
                  ))}
                  <li className={styles.menuA} onClick={handleLogout}>
                    <LogOut size={15} className={styles.menuItemIcon} />
                    {t("LOGOUT")}
                  </li>
                  {version && (
                    <li className={styles.menuVersion}>v{version}</li>
                  )}
                </ul>
              </>
            )}
          </li>

          {/* Hamburger button */}
          <button
            className={styles.hamburger}
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile panel */}
        {mobileOpen && (
          <div
            className={styles.mobilePanel}
            onClick={() => setMobileOpen(false)}
          >
            <ul className={styles.mobilePanelMenu}>{children}</ul>
            {user && (
              <div className={styles.mobilePanelUser}>
                <div className={styles.mobilePanelUserName}>
                  <UserCircle size={18} style={{ marginRight: "0.4rem" }} />
                  {user.firstname} {user.lastname}
                </div>
                {onSettingsClick && (
                  <div
                    className={styles.mobilePanelItem}
                    onClick={onSettingsClick}
                  >
                    <Settings size={15} className={styles.menuItemIcon} />
                    {t("MENU_MANAGEMENT")}
                  </div>
                )}
                {userMenuItems?.map((item) => (
                  <div
                    key={item.label}
                    className={styles.mobilePanelItem}
                    onClick={item.onClick}
                  >
                    <span className={styles.menuItemIcon}>{item.icon}</span>
                    {item.label}
                  </div>
                ))}
                <div className={styles.mobilePanelItem} onClick={handleLogout}>
                  <LogOut size={15} className={styles.menuItemIcon} />
                  {t("LOGOUT")}
                </div>
                {version && (
                  <div className={styles.menuVersion}>v{version}</div>
                )}
              </div>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
